<template>
    <div class="ftp-upload-tile">
        <div class="panel">
            <div class="panel-header">
                <h2>LEO File Upload</h2>
            </div>
            <div class="panel-body">
                <!-- File Selection -->
                <div class="form-row">
                    <div class="form-column">
                        <label for="file" class="form-label">Select File to Upload (Max {{ maxFiles }} files)</label>
                        <input type="file" id="file" @change="handleFileUpload" class="custom-file-input"
                            ref="fileInput" multiple accept=".pdf">
                        <div v-if="selectedFiles.length > 0" class="file-info">
                            <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                                <strong>{{ file.name }}</strong> ({{ formatFileSize(file.size) }})
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Upload Progress -->
                <div v-if="uploadProgress > 0 && uploadProgress < 100" class="progress-container">
                    <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                    <div class="progress-text">{{ uploadProgress }}% uploaded</div>
                </div>

                <!-- Status Messages -->
                <div v-if="statusMessage" :class="['status-message', statusType]">
                    {{ statusMessage }}
                </div>

                <!-- Action Buttons -->
                <div class="form-actions" align="center">
                    <button class="btn btn-upload" @click="uploadToFtp" :disabled="isUploading || !canUpload">
                        <span v-if="isUploading">Uploading...</span>
                        <span v-else>Upload</span>
                    </button>
                    <button type="button" class="btn btn-secondary" @click="resetForm" :disabled="isUploading">
                        Reset
                    </button>
                </div>

                <!-- Upload History -->
                <div class="upload-history" v-if="uploadHistory.length > 0">
                    <h3>Recent Uploads</h3>
                    <table class="history-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>File Name</th>
                                <th>Remote Path</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in uploadHistory" :key="index">
                                <td>{{ item.timestamp }}</td>
                                <td>{{ item.fileName }}</td>
                                <td>{{ item.remotePath }}</td>
                                <td :class="['status', item.status]">{{ item.status }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FtpUpload',
    data() {
        return {
            maxFiles: 10,
            // API endpoint - change based on environment
            apiUrl: 'https://ftpapi.lardnernorth.in/api/upload-ftp',
            formData: {
                ftpHost: 'fisystechnology.in',
                ftpPort: 21,
                ftpUsername: 'fisystechnology_fisys_ftp_admin',
                ftpPassword: 'zD5i$v9@Y',
                remotePath: '/'
            },
            selectedFiles: [],
            isUploading: false,
            uploadProgress: 0,
            statusMessage: '',
            statusType: '', // 'success' or 'error'
            uploadHistory: []
        };
    },
    computed: {
        canUpload() {
            return this.selectedFiles.length > 0;
        }
    },
    methods: {
        handleFileUpload(event) {
            const files = Array.from(event.target.files);
            if (files.length > this.maxFiles) {
                this.showStatus(`Maximum ${this.maxFiles} files allowed. Please select up to ${this.maxFiles} files only.`, 'error');
                this.$refs.fileInput.value = '';
                return;
            }
            this.selectedFiles = files;
            if (this.selectedFiles.length > 0) {
                this.statusMessage = '';
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        async uploadToFtp() {
            if (!this.canUpload) {
                this.showStatus('Please fill all required fields and select a file', 'error');
                return;
            }
            this.isUploading = true;
            this.uploadProgress = 0;
            this.statusMessage = '';

            try {
                let successCount = 0;
                const totalFiles = this.selectedFiles.length;

                for (let i = 0; i < totalFiles; i++) {
                    const file = this.selectedFiles[i];
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('ftpHost', this.formData.ftpHost);
                    formData.append('ftpPort', this.formData.ftpPort);
                    formData.append('ftpUsername', this.formData.ftpUsername);
                    formData.append('ftpPassword', this.formData.ftpPassword);
                    formData.append('remotePath', this.formData.remotePath);

                    try {
                        const response = await fetch(this.apiUrl, {
                            method: 'POST',
                            body: formData
                        });

                        // Check if response is ok
                        if (!response.ok) {
                            console.error(`HTTP error! status: ${response.status}`);
                            const text = await response.text();
                            console.error('Response body:', text.substring(0, 200));
                            this.addToHistory('Failed', file.name, this.formData.remotePath + '/' + file.name);
                            this.uploadProgress = Math.round(((i + 1) / totalFiles) * 100);
                            continue;
                        }

                        // Check content type
                        const contentType = response.headers.get('content-type');
                        if (!contentType || !contentType.includes('application/json')) {
                            console.error('Expected JSON but got:', contentType);
                            const text = await response.text();
                            console.error('Response body:', text.substring(0, 200));
                            this.addToHistory('Failed', file.name, this.formData.remotePath + '/' + file.name);
                            this.uploadProgress = Math.round(((i + 1) / totalFiles) * 100);
                            continue;
                        }

                        const result = await response.json();

                        if (result.success) {
                            successCount++;
                            this.addToHistory('Success', file.name, this.formData.remotePath + '/' + file.name);
                        } else {
                            this.addToHistory('Failed', file.name, this.formData.remotePath + '/' + file.name);
                        }

                        // Update progress
                        this.uploadProgress = Math.round(((i + 1) / totalFiles) * 100);

                    } catch (error) {
                        console.error(`Failed to upload ${file.name}:`, error);
                        this.addToHistory('Failed', file.name, this.formData.remotePath + '/' + file.name);
                        this.uploadProgress = Math.round(((i + 1) / totalFiles) * 100);
                    }
                }

                if (successCount === totalFiles) {
                    this.showStatus(`All ${totalFiles} file(s) uploaded successfully!`, 'success');
                } else if (successCount > 0) {
                    this.showStatus(`${successCount} of ${totalFiles} file(s) uploaded successfully`, 'warning');
                } else {
                    this.showStatus('Upload failed. Please check your connection and try again', 'error');
                }

                if (successCount > 0) {
                    setTimeout(() => {
                        this.resetForm();
                    }, 3000);
                }

            } catch (error) {
                console.error('Upload error:', error);
                this.showStatus(
                    'Failed to connect to upload server. Please check your connection and try again',
                    'error'
                );
            } finally {
                this.isUploading = false;
                setTimeout(() => {
                    this.uploadProgress = 0;
                }, 1000);
            }
        },

        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsArrayBuffer(file);
            });
        },

        uploadFile(url, data, onProgress) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        const progress = (e.loaded / e.total) * 100;
                        onProgress(progress);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(xhr.response);
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => reject(new Error('Network error')));
                xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

                xhr.open('PUT', url, true);
                xhr.send(data);
            });
        },

        showStatus(message, type) {
            this.statusMessage = message;
            this.statusType = type;
        },

        addToHistory(status, fileName, remotePath) {
            const now = new Date();
            const timestamp = now.toLocaleString();
            this.uploadHistory.unshift({
                timestamp,
                fileName: fileName || (this.selectedFiles[0]?.name || 'Unknown'),
                remotePath: remotePath || this.formData.remotePath,
                status
            });
            // Keep only last 10 entries
            if (this.uploadHistory.length > 10) {
                this.uploadHistory.pop();
            }
        },

        resetForm() {
            this.selectedFiles = [];
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
            this.uploadProgress = 0;
            this.statusMessage = '';
            this.statusType = '';
        }
    }
};
</script>

<style scoped>
.ftp-upload-tile {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.panel {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.panel-header {
    padding: 20px;
    border-bottom: 2px solid #e0e0e0;
}

.panel-header h2 {
    margin: 0;
    color: #333;
    font-size: 24px;
}

.panel-body {
    padding: 20px;
}

.form-row {
    margin-bottom: 20px;
}

.form-row.two-inline {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.form-column {
    display: flex;
    flex-direction: column;
}

.form-label {
    font-weight: 600;
    margin-bottom: 8px;
    color: #555;
    font-size: 14px;
}

.custom-input,
.custom-file-input {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.3s;
}

.custom-input:focus,
.custom-file-input:focus {
    outline: none;
    border-color: #4CAF50;
}

.form-hint {
    margin-top: 5px;
    color: #888;
    font-size: 12px;
}

.file-info {
    margin-top: 10px;
    padding: 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 14px;
}

.file-item {
    padding: 5px 0;
    border-bottom: 1px solid #e0e0e0;
}

.file-item:last-child {
    border-bottom: none;
}

.progress-container {
    margin: 20px 0;
}

.progress-bar {
    width: 100%;
    height: 30px;
    background: #e0e0e0;
    border-radius: 15px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4CAF50, #45a049);
    transition: width 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.progress-text {
    text-align: center;
    margin-top: 8px;
    font-weight: 600;
    color: #333;
}

.status-message {
    padding: 12px;
    border-radius: 4px;
    margin: 15px 0;
    font-weight: 500;
}

.status-message.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-message.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.status-message.warning {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
}

.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 15px;
    justify-content: center;
}

.btn {
    padding: 12px 30px;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
}

.btn-upload {
    background: #4CAF50;
    color: white;
}

.btn-upload:hover:not(:disabled) {
    background: #45a049;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
    background: #6c757d;
    color: white;
}

.btn-secondary:hover:not(:disabled) {
    background: #5a6268;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.upload-history {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid #e0e0e0;
}

.upload-history h3 {
    margin-bottom: 15px;
    color: #333;
}

.history-table {
    width: 100%;
    border-collapse: collapse;
}

.history-table th,
.history-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
}

.history-table th {
    background: #f5f5f5;
    font-weight: 600;
    color: #555;
}

.history-table .status {
    font-weight: 600;
}

.history-table .status.Success {
    color: #28a745;
}

.history-table .status.Failed,
.history-table .status.Error {
    color: #dc3545;
}

@media (max-width: 768px) {
    .form-row.two-inline {
        grid-template-columns: 1fr;
    }
}
</style>
